import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals';
import { useCreateEncounterFromAppointmentMutation } from '@features/appointments';
import { selectActiveWorkflowEncounterId } from '@features/encounters';
import { bindWaitingRoomLaunchToEncounter } from '@features/telehealth/store';
import type { TelehealthEncounterRouteState } from '@features/telehealth/types';
import { useAppDispatch, useAppSelector, useModal } from '@store/hooks';

import { useRecordingContext } from '../context/RecordingContext';

import { useAppointmentCollision } from './useAppointmentCollision';
import { useShouldUseEncounters } from './useShouldUseEncounters';

/**
 * Type guard to safely check if an object has an encounter_id property
 */
function hasEncounterId(encounter: unknown): encounter is { encounter_id: string } {
  return (
    encounter !== null &&
    typeof encounter === 'object' &&
    'encounter_id' in encounter &&
    typeof (encounter as Record<string, unknown>).encounter_id === 'string'
  );
}

interface StartRecordingOptions {
  forceAppointmentSelection?: boolean;
  routeState?: TelehealthEncounterRouteState;
  bindWaitingRoomLaunch?: boolean;
}

/**
 * Hook for managing recording session navigation and context.
 * Handles routing to the appropriate recording interface based on feature flags
 * and detects appointment conflicts to suggest the best recording context.
 */
export const useRecordingSession = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const shouldUseEncounters = useShouldUseEncounters();
  const { open: openModal } = useModal();
  const {
    appointmentData,
    isLoading,
    recommendation,
    hasCollision,
    eligibleCurrentAppointments,
    eligibleOvertimeAppointments,
    eligibleNextAppointments,
  } = useAppointmentCollision();
  const [createEncounterFromAppointment] = useCreateEncounterFromAppointmentMutation();

  // Get recording status and active encounter for bypass logic
  const { recordingStatus } = useRecordingContext();
  const activeWorkflowEncounterId = useAppSelector(selectActiveWorkflowEncounterId) as
    | string
    | null
    | undefined;

  const recordingPath = useMemo(
    () => (shouldUseEncounters ? '/encounters/active' : '/record'),
    [shouldUseEncounters],
  );

  const startRecording = useCallback(
    async (options?: StartRecordingOptions) => {
      const navigateToEncounter = (encounterId: string, replace = false) => {
        if (options?.bindWaitingRoomLaunch) {
          dispatch(bindWaitingRoomLaunchToEncounter(encounterId));
        }
        navigate(`/encounters/${encounterId}`, { replace, state: options?.routeState });
      };

      const navigateToActiveEncounter = () => {
        navigate('/encounters/active', { state: options?.routeState });
      };

      // If encounters not enabled, use legacy flow
      if (!shouldUseEncounters) {
        navigate('/record');
        return;
      }

      // If actively recording, bypass collision detection and navigate to active encounter
      // This prevents showing collision modal when user is already recording
      if (recordingStatus.isRecording) {
        if (activeWorkflowEncounterId) {
          // User is recording on a specific encounter - take them there
          navigateToEncounter(activeWorkflowEncounterId);
        } else {
          // Edge case: recording but no encounter ID (shouldn't happen in normal flow)
          // Navigate to /encounters/active and let EncounterView handle redirection
          navigateToActiveEncounter();
        }
        return;
      }

      // Check for appointment collision
      if (isLoading) {
        // If still loading, proceed without collision check
        navigateToActiveEncounter();
        return;
      }

      const eligibleAppointmentCount =
        eligibleCurrentAppointments.length +
        eligibleOvertimeAppointments.length +
        eligibleNextAppointments.length;

      if (options?.forceAppointmentSelection && eligibleAppointmentCount > 0) {
        openModal(MODAL_TYPES.APPOINTMENT_SELECTION, {
          eligibleCurrent: eligibleCurrentAppointments,
          eligibleOvertime: eligibleOvertimeAppointments,
          eligibleNext: eligibleNextAppointments,
          recommendation,
          telehealthLaunch: true,
        });
        return;
      }

      // If high confidence match, auto-navigate to the appointment's encounter
      if (recommendation?.confidence === 'high') {
        const appointment = recommendation.appointment;
        try {
          // Use type guard to safely check for encounter_id
          if (hasEncounterId(appointment.encounter)) {
            // Encounter already exists, navigate to it
            navigateToEncounter(appointment.encounter.encounter_id);
          } else {
            // Create encounter and navigate
            const encounter = await createEncounterFromAppointment({
              appointmentId: appointment.appointment_id,
            }).unwrap();
            if (encounter?.encounter_id) {
              navigateToEncounter(encounter.encounter_id);
            } else {
              throw new Error('Failed to get encounter ID');
            }
          }
        } catch (error) {
          console.error('Failed to create encounter from appointment:', error);
          // Fallback to unscheduled recording
          navigateToActiveEncounter();
        }
        return;
      }

      // If medium confidence or multiple options, show selection modal
      if (hasCollision) {
        openModal(MODAL_TYPES.APPOINTMENT_SELECTION, {
          eligibleCurrent: eligibleCurrentAppointments,
          eligibleOvertime: eligibleOvertimeAppointments,
          eligibleNext: eligibleNextAppointments,
          recommendation,
          telehealthLaunch: Boolean(options?.routeState?.telehealthLaunch),
        });
        return;
      }

      // No collisions, go to active encounter
      navigateToActiveEncounter();
    },
    [
      navigate,
      dispatch,
      shouldUseEncounters,
      recordingStatus,
      activeWorkflowEncounterId,
      isLoading,
      recommendation,
      hasCollision,
      openModal,
      createEncounterFromAppointment,
      eligibleCurrentAppointments,
      eligibleOvertimeAppointments,
      eligibleNextAppointments,
    ],
  );

  return {
    startRecording,
    recordingPath,
    appointmentCollision: appointmentData,
    hasAppointmentCollision: hasCollision,
    isLoadingAppointments: isLoading,
  };
};
